import * as React from 'react';
import { jsx, css } from '@emotion/react';
import { mediaQueries, autoResponsiveClamp } from 'Src/me/pages/guest/proposals/new/React/viewHelpers';
import { wait } from 'Src/me/pages/guest/proposals/new/utils';
import { StepArrowSvg } from './assets/StepArrow.svg';
import { MainvisualPropsType } from '../Mainvisual';
import { QUESTION_KEY_NAMES, QUESTIONS } from './consts/questions';
import { ANSWER_KEY_NAMES, ANSWER_GROUPS, lastAnswerKeyName } from './consts/answerGroup';

const { tablet, mobile } = mediaQueries();

const KEY_NAME = 'MainvisualConstructionCategorySearchWidget';

type MainvisualConstructionCategorySearchWidgetPropsType = MainvisualPropsType;

export const MainvisualConstructionCategorySearchWidget = (props: MainvisualConstructionCategorySearchWidgetPropsType) => {
  const { userAnswers, commonAnswerHandler, onApplyHandler: onApplyHandlerFromProps } = props;
  const [currentQuestionKeyName, setCurrentQuestionKeyName] = React.useState<typeof QUESTION_KEY_NAMES[number]>('area');
  const [questions, setQuestions] = React.useState(QUESTIONS);
  const [currentAnswerKeyName, setCurrentAnswerKeyName] = React.useState<typeof ANSWER_KEY_NAMES[number]>('region');
  const [answerGroups, setAnswerGroups] = React.useState(ANSWER_GROUPS);
  const [isPanelDisabled, setIsPanelDisabled] = React.useState(false);
  const [isApplyButtonActive, setIsApplyButtonActive] = React.useState(false);
  const currentAnswerGroup = answerGroups.find((group) => group.keyName === currentAnswerKeyName);

  const onApplyHandler = React.useCallback(() => {
    // ユーザー回答を格納
    answerGroups.forEach((group) => {
      const { keyName, paramName, question, answers } = group;
      const checkedAnswer = answers.find((answer) => answer.checked);

      if (checkedAnswer) {
        const { answerText, value } = checkedAnswer;

        userAnswers.push({
          keyName,
          paramName,
          question,
          answer: answerText,
          value,
        });

        commonAnswerHandler(paramName, answerText);
      }
    });

    // constructionCategoryの値を設定
    const constructionPartsAnswer = userAnswers.find((answer) => answer.paramName === 'constructionParts');
    const constructionTypeAnswer = userAnswers.find((answer) => answer.paramName === 'constructionType');
    const constructionCategory = {
      '外壁と屋根': {
        '塗装': '5',
        '張り替え': '6',
        '補修': '5',
        '決まっていない': '5',
      }?.[constructionTypeAnswer?.answer] || '5',
      '外壁': {
        '塗装': '1',
        '張り替え': '2',
        '補修': '1',
        '決まっていない': '1',
      }?.[constructionTypeAnswer?.answer] || '1',
      '決まっていない': {
        '塗装': '1',
        '張り替え': '2',
        '補修': '1',
        '決まっていない': '1',
      }?.[constructionTypeAnswer?.answer] || '1',
    }?.[constructionPartsAnswer?.answer] || '1';
    userAnswers.push({
      keyName: 'constructionCategory',
      paramName: 'constructionCategory',
      question: '',
      answer: constructionCategory,
      value: constructionCategory,
    });

    // 外壁と屋根の場合は屋根の工事種別も格納
    if (constructionPartsAnswer?.answer === '外壁と屋根') {
      userAnswers.push({
        keyName: 'roofConstructionType',
        paramName: 'roofConstructionType',
        question: '屋根の工事種別は',
        answer: constructionTypeAnswer?.answer || '塗装',
        value: constructionTypeAnswer?.answer || '塗装',
      });
    }

    onApplyHandlerFromProps();
  }, [userAnswers, answerGroups, commonAnswerHandler, onApplyHandlerFromProps]);

  return (
    <div
      css={css`
        padding: 20px 26px 30px;
        background-color: #bae8e8;
        border-radius: 10px;
        box-shadow: 0 3px 6px rgb(0 0 0 / 0.16);

        ${tablet(({ setAutomaticDesignBasedSize }) => css`
          padding: ${setAutomaticDesignBasedSize(20, 14)} ${setAutomaticDesignBasedSize(26, 21)} ${setAutomaticDesignBasedSize(30, 23)};
        `)}

        ${mobile(({ setAutomaticDesignBasedSize }) => css`
          padding: ${setAutomaticDesignBasedSize(20, 14)} ${setAutomaticDesignBasedSize(26, 21)} ${setAutomaticDesignBasedSize(30, 23)};
        `)}
      `}
    >
      <p
        css={css`
          color: #272343;
          font-size: 19px;
          font-weight: bold;
          text-align: center;
          margin-bottom: 18px;
          padding: 6px 0;
          background-color: #ffec87;
          border-radius: 59px;

          ${tablet(({ setAutomaticDesignBasedSize }) => css`
            font-size: ${setAutomaticDesignBasedSize(19, 16)};
            margin-bottom: ${setAutomaticDesignBasedSize(18, 10)};
          `)}

          ${mobile(({ setAutomaticDesignBasedSize }) => css`
            font-size: ${setAutomaticDesignBasedSize(19, 16)};
            margin-bottom: ${setAutomaticDesignBasedSize(18, 10)};
          `)}
        `}
      >
        基本情報で業者を探す
      </p>
      <div
        css={css`
          margin-bottom: 12px;

          ${mobile(({ setAutomaticDesignBasedSize }) => css`
            margin-bottom: ${setAutomaticDesignBasedSize(12, 9)};
          `)}
        `}
      >
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
          `}
        >
          {questions.map((item, index) => {
            const { id, keyName, question } = item;
            const isLastElement = index === questions.length - 1;

            return (
              <div
                key={id}
                css={css`
                  width: calc(4em + 55px);
                  padding-right: 55px;
                  position: relative;

                  &:last-of-type {
                    width: 4em;
                    padding-right: 0;
                  }
                `}
              >
                <p
                  css={css`
                    color: #9f9f9f;
                    font-size: 15px;
                    font-weight: bold;
                    text-align: center;
                    white-space: nowrap;
                    padding-bottom: 5px;
                    position: relative;

                    ${tablet(({ setAutomaticDesignBasedSize }) => css`
                      font-size: ${setAutomaticDesignBasedSize(15, 12)};
                    `)}

                    ${mobile(({ setAutomaticDesignBasedSize }) => css`
                      font-size: ${setAutomaticDesignBasedSize(15, 12)};
                    `)}

                    ${currentQuestionKeyName === keyName && css`
                      color: #041156;

                      &::before {
                        content: "";
                        width: 100%;
                        height: 5px;
                        background-color: #ffec87;
                        border-radius: 10px;
                        position: absolute;
                        bottom: 0;
                        left: 50%;
                        transform: translateX(-50%);
                      }
                    `}
                  `}
                >
                  {question}
                </p>
                {!isLastElement && (
                  <div
                    css={css`
                      font-size: 0;
                      width: 13px;
                      position: absolute;
                      top: 5px;
                      right: 20px;
                    `}
                  >
                    <StepArrowSvg />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div
        css={css`
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-template-rows: repeat(4, 1fr);
          gap: 10px;
          min-height: 290px;
          margin-bottom: 20px;

          ${mobile(({ setAutomaticDesignBasedSize }) => css`
            min-height: ${setAutomaticDesignBasedSize(290, 259)};
            margin-bottom: ${setAutomaticDesignBasedSize(20, 12)};
          `)}

          ${(currentAnswerKeyName === 'constructionParts' || currentAnswerKeyName === 'wallConstructionType') && css`
            grid-template-rows: repeat(2, 1fr);
          `}

          ${(isPanelDisabled && currentAnswerKeyName !== lastAnswerKeyName) && css`
            opacity: 0;
            visibility: hidden;
            transition: opacity 0.4s 0.1s, visibility 0.4s 0.1s;
          `}
        `}
      >
        {currentAnswerGroup.answers.map((answer) => {
          const { id, answerText, value, checked, iconElement } = answer;
          const onClickHandler = async (event: React.MouseEvent<HTMLButtonElement>) => {
            const nextQuestionIndex = QUESTION_KEY_NAMES.findIndex((key) => key === currentQuestionKeyName) + 1;
            const nextQuestionKeyName = QUESTION_KEY_NAMES[nextQuestionIndex];
            const nextAnswerGroupIndex = ANSWER_KEY_NAMES.findIndex((key) => key === currentAnswerKeyName) + 1;
            const nextAnswerGroup = ANSWER_KEY_NAMES[nextAnswerGroupIndex];

            setIsPanelDisabled(true);

            // チェック状態を更新
            setAnswerGroups((prev) => prev.map((group) => {
              if (group.keyName === currentAnswerKeyName) {
                const { answers } = group;
                return {
                  ...group,
                  answers: answers.map((answer) => {
                    if (answer.id === id) {
                      return {
                        ...answer,
                        checked: true,
                      };
                    }

                    return answer;
                  }),
                };
              }

              return group;
            }));

            if (currentAnswerKeyName !== lastAnswerKeyName) {
              await wait(0.5); // アニメーション待ち時間
            }

            // 質問＆回答の状態を更新
            if (currentAnswerKeyName === 'region') {
              setAnswerGroups((prev) => prev.map((group) => {
                if (group.keyName === 'prefecture') {
                  const { answers: prefectureAnswers } = group;
                  return {
                    ...group,
                    answers: prefectureAnswers.filter((prefecture) => prefecture.regionId === id),
                  };
                }

                return group;
              }));
              setCurrentAnswerKeyName('prefecture');
            } else {
              if (nextQuestionKeyName) {
                setCurrentQuestionKeyName(nextQuestionKeyName);
              }

              if (nextAnswerGroup) {
                setCurrentAnswerKeyName(nextAnswerGroup);
              }
            }

            if (currentAnswerKeyName !== lastAnswerKeyName) {
              setIsPanelDisabled(false);
            } else {
              setIsApplyButtonActive(true);
            }
          };

          return (
            <button
              key={id}
              type='button'
              value={value}
              onClick={onClickHandler}
              id={`ga_event_${KEY_NAME}_${currentAnswerKeyName}_${id}`}
              css={css`
                color: #041156;
                font-size: 18px;
                font-weight: bold;
                text-align: center;
                padding: 17px 0;
                background-color: #fff;
                border: 2px solid #041156;
                border-radius: 4px;

                ${tablet(({ setAutomaticDesignBasedSize }) => css`
                  font-size: ${setAutomaticDesignBasedSize(18, 16)};
                `)}

                ${mobile(({ setAutomaticDesignBasedSize }) => css`
                  font-size: ${setAutomaticDesignBasedSize(18, 16)};
                  padding: ${setAutomaticDesignBasedSize(17, 14)} 0;
                `)}

                &:hover {
                  background-color: #fff7c9;

                  ${tablet(() => css`
                    background-color: #fff;
                  `)}
                }

                ${checked && css`
                  background-color: #fff7c9;

                  ${tablet(() => css`
                    background-color: #fff7c9 !important;
                  `)}
                `}

                ${isPanelDisabled && css`
                  pointer-events: none;
                `}

                ${currentAnswerKeyName === 'constructionParts' && css`
                  ${id === 1 && css`
                    grid-column: 1 / 3;
                  `}
                `}
              `}
            >
              {iconElement}
              {answerText}
            </button>
          );
        })}
      </div>
      <button
        type='button'
        disabled={!isApplyButtonActive}
        onClick={onApplyHandler}
        id={`ga_event_${KEY_NAME}_submit_button`}
        css={css`
          color: #fff;
          font-size: 20px;
          font-weight: bold;
          text-align: center;
          padding: 22px 0;
          background-color: #041156;
          border: 1px solid #041156;
          border-radius: 6px;

          ${mobile(({ setAutomaticDesignBasedSize }) => css`
            font-size: ${setAutomaticDesignBasedSize(20, 18)};
            padding: ${setAutomaticDesignBasedSize(22, 19)} 0;
          `)}

          &:disabled {
            color: #b9b9b9;
            background-color: #e8e8e8;
            border: 1px solid #bfbfbf;
            pointer-events: none;
          }

          &:hover {
            color: #272343;
            background-color: #ffd803;
            border: 1px solid #bfbfbf;
          }
        `}
      >
        検索する
      </button>
    </div>
  );
};
